<template>
  <div class="claim-timeline-step" data-test-id="claim-timeline-step">
    <div class="copy">
      <Typography variant="h-3">First, let's set your timeline.</Typography>
      <Typography variant="body-1">
        These dates define when eligible activity counts toward the program.
      </Typography>
    </div>

    <v-form ref="form" class="form">
      <div class="date-section">
        <Typography variant="subtitle-1">Program Dates</Typography>
        <div class="date-row">
          <DateTimeField
            class="date-input"
            label="Program start date*"
            :value="startDate"
            :rules="startRules"
            @input="$emit('update:startDate', $event)"
          />
          <Typography variant="h-5" as="span" class="pt-4">–</Typography>
          <DateTimeField
            ref="endField"
            class="date-input"
            label="Program end date*"
            :value="endDate"
            :min="startDate"
            :rules="endRules"
            @input="$emit('update:endDate', $event)"
          />
        </div>
      </div>

      <v-divider />

      <div class="copy">
        <Typography variant="h-5">Now, let's set up your claim window.</Typography>
        <Typography variant="body-1">
          This is when participants can submit claims — it usually opens when the program ends.
        </Typography>
      </div>

      <div class="date-section">
        <Typography variant="subtitle-1">Claim Window</Typography>
        <div class="date-row">
          <DateTimeField
            class="date-input"
            label="Claims open*"
            hint="Defaults to program start date — participants can start submitting right away"
            :value="claimsOpenDate"
            :rules="claimsOpenRules"
            @input="$emit('update:claimsOpenDate', $event)"
          />
          <Typography variant="h-5" as="span" class="pt-4">–</Typography>
          <DateTimeField
            ref="claimsCloseField"
            class="date-input"
            label="Claims close*"
            hint="Defaults to 30 days after the program ends"
            :value="claimsCloseDate"
            :min="claimsOpenDate"
            :rules="claimsCloseRules"
            @input="$emit('update:claimsCloseDate', $event)"
          />
        </div>
      </div>

      <v-divider />

      <div class="copy">
        <Typography variant="h-5">Next, set your reward deadline.</Typography>
        <Typography variant="body-1">
          This is the last day the program calculates rewards. Any claim you want to pay out,
          including claims in audit, needs to be approved by this date.
        </Typography>
      </div>

      <div class="date-section">
        <Typography variant="subtitle-1">Reward Cutoff</Typography>
        <div class="date-row">
          <DateTimeField
            ref="rewardDeadlineField"
            class="date-input"
            label="Last day to approve & reward*"
            hint="Defaults to 30 days after claims close"
            :value="ruleCalculationWindow"
            :min="rewardDeadlineMin"
            :rules="rewardDeadlineRules"
            @input="$emit('update:ruleCalculationWindow', $event)"
          />
          <div class="date-input" aria-hidden="true" />
        </div>
      </div>

      <v-divider />

      <div class="copy">
        <Typography variant="h-5">
          Finally, when should the dashboard program summary card be active?
        </Typography>
        <Typography variant="body-1">
          Before these dates, the card isn't visible to participants yet. After them, it's archived.
        </Typography>
      </div>

      <div class="date-section">
        <Typography variant="subtitle-1">Card Visibility</Typography>
        <div class="date-row">
          <DateTimeField
            ref="cardActiveFromField"
            class="date-input"
            label="Card visible on*"
            :value="cardActiveFrom"
            :max="startDate"
            :rules="cardActiveFromRules"
            @input="$emit('update:cardActiveFrom', $event)"
          />
          <Typography variant="h-5" as="span" class="pt-4">–</Typography>
          <DateTimeField
            ref="cardArchiveField"
            class="date-input"
            label="Card archive after*"
            hint="Defaults to your reward cutoff date. You can override it any time"
            :value="cardArchiveAfter"
            :min="claimsCloseDate"
            :rules="cardArchiveRules"
            @input="$emit('update:cardArchiveAfter', $event)"
          />
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import DateTimeField from "@/components/generic/form-inputs/DateTimeField.vue";
import {
  clearsRewardCutoffFloor,
  getPayoutDate,
  getRewardCutoffFloor,
  MIN_REWARD_CUTOFF_MINUTES,
} from "@/utils/payoutDate";
import moment from "moment";

const VALUE_FORMAT = "YYYY-MM-DDTHH:mm";
const CLAIM_CLOSE_OFFSET_DAYS = 30;
const RULE_CALCULATION_WINDOW_DAYS = 30;

export default {
  name: "ClaimTimelineStep",
  components: {
    Typography,
    DateTimeField,
  },
  props: {
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    claimsOpenDate: {
      type: String,
      default: null,
    },
    claimsCloseDate: {
      type: String,
      default: null,
    },
    cardActiveFrom: {
      type: String,
      default: null,
    },
    cardArchiveAfter: {
      type: String,
      default: null,
    },
    ruleCalculationWindow: {
      type: String,
      default: null,
    },
    payoutCadence: {
      type: String,
      default: null,
    },
    payoutEndOffsetDays: {
      type: Number,
      default: null,
    },
    payoutLocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:startDate",
    "update:endDate",
    "update:claimsOpenDate",
    "update:claimsCloseDate",
    "update:cardActiveFrom",
    "update:cardArchiveAfter",
    "update:ruleCalculationWindow",
  ],
  computed: {
    startRules() {
      return [() => !!this.startDate || "A start date is required"];
    },
    endRules() {
      return [
        () => !!this.endDate || "An end date is required",
        () => this.isAfter(this.endDate, this.startDate) || "End date must be after start date",
      ];
    },
    claimsOpenRules() {
      return [() => !!this.claimsOpenDate || "A claims open date is required"];
    },
    claimsCloseRules() {
      return [
        () => !!this.claimsCloseDate || "A claims close date is required",
        () =>
          this.isAfter(this.claimsCloseDate, this.claimsOpenDate) ||
          "Claims close must be after claims open",
      ];
    },
    cardActiveFromRules() {
      return [
        () => !!this.cardActiveFrom || "A card visible on date is required",
        () =>
          this.isOnOrAfter(this.startDate, this.cardActiveFrom) ||
          "Card visible on must be on or before the program start date",
      ];
    },
    cardArchiveRules() {
      return [
        () => !!this.cardArchiveAfter || "A card archive date is required",
        () =>
          this.isAfter(this.cardArchiveAfter, this.cardActiveFrom) ||
          "Card archive must be after card visible",
        () =>
          this.isOnOrAfter(this.cardArchiveAfter, this.claimsCloseDate) ||
          "Card archive must be on or after the claims close date",
      ];
    },
    rewardDeadlineRules() {
      return [
        () => !!this.ruleCalculationWindow || "A reward deadline is required",
        () =>
          this.isOnOrAfter(this.ruleCalculationWindow, this.claimsCloseDate) ||
          "Reward deadline must be on or after the claims close date",
        () =>
          clearsRewardCutoffFloor(this.ruleCalculationWindow, this.rewardCutoffBase) ||
          this.rewardCutoffError,
      ];
    },
    // The sweep's real boundary is the program end date. The payout date is that plus
    // the offset, so when one exists it is the later of the two and stays the floor
    // the user is shown -- the reward step measures from the same base.
    rewardCutoffBase() {
      return this.payoutDate ?? (this.endDate || null);
    },
    rewardCutoffFloor() {
      return getRewardCutoffFloor(this.rewardCutoffBase);
    },
    // Only constrains the native picker; the visible field is typed, so the rules
    // above remain the real guard.
    rewardDeadlineMin() {
      const floor = this.rewardCutoffFloor?.format(VALUE_FORMAT);
      if (!floor) return this.claimsCloseDate;
      if (!this.claimsCloseDate) return floor;
      return moment(floor).isAfter(this.claimsCloseDate) ? floor : this.claimsCloseDate;
    },
    rewardCutoffError() {
      const date = this.rewardCutoffFloor?.format("MM/DD/YYYY, h:mm A");
      if (!this.payoutDate)
        return `Reward cutoff must be at least ${MIN_REWARD_CUTOFF_MINUTES} minutes after the program end date (${date} or later).`;
      return this.payoutLocked
        ? `Payout dates can't change on live programs. Reward cutoff must be ${date} or later.`
        : `Reward cutoff must be ${date} or later, at least ${MIN_REWARD_CUTOFF_MINUTES} minutes past your payout date. To change it, update the payout date first.`;
    },
    payoutDate() {
      if (this.payoutCadence !== "PROGRAM_END") return null;
      return getPayoutDate(this.endDate, this.payoutEndOffsetDays);
    },
  },
  watch: {
    // The floor moves with the end date as well as the payout date, and for an
    // IMMEDIATE program there is no payout date at all.
    rewardCutoffFloor() {
      if (this.ruleCalculationWindow) this.$refs.rewardDeadlineField?.validate(true);
    },
    startDate(next, prev) {
      if (this.endDate) this.$refs.endField?.validate(true);
      if (this.cardActiveFrom) this.$refs.cardActiveFromField?.validate(true);
      if (next === prev) return;
      if (this.followsDefault(this.claimsOpenDate, prev)) {
        this.$emit("update:claimsOpenDate", next);
      }
    },
    endDate(next, prev) {
      this.$refs.endField?.validate(true);
      if (next === prev) return;
      const prevDefault = this.claimCloseDefault(prev);
      if (this.followsDefault(this.claimsCloseDate, prevDefault)) {
        this.$emit("update:claimsCloseDate", this.claimCloseDefault(next));
      }
    },
    claimsOpenDate() {
      if (this.claimsCloseDate) this.$refs.claimsCloseField?.validate(true);
    },
    claimsCloseDate(next, prev) {
      this.$refs.claimsCloseField?.validate(true);
      if (this.cardArchiveAfter) this.$refs.cardArchiveField?.validate(true);
      if (this.ruleCalculationWindow) this.$refs.rewardDeadlineField?.validate(true);
      if (next === prev) return;
      const prevWindow = this.calculationWindowDefault(prev);
      if (this.followsDefault(this.ruleCalculationWindow, prevWindow)) {
        this.$emit("update:ruleCalculationWindow", this.calculationWindowDefault(next));
      }
    },
    ruleCalculationWindow(next, prev) {
      if (next === prev) return;
      if (this.followsDefault(this.cardArchiveAfter, prev)) {
        this.$emit("update:cardArchiveAfter", next);
      }
    },
    cardActiveFrom() {
      this.$refs.cardActiveFromField?.validate(true);
      if (this.cardArchiveAfter) this.$refs.cardArchiveField?.validate(true);
    },
    cardArchiveAfter() {
      this.$refs.cardArchiveField?.validate(true);
    },
  },
  methods: {
    // Empty, or still sitting on the previous default — keep following it.
    followsDefault(current, previousDefault) {
      return !current || current === previousDefault;
    },
    claimCloseDefault(endDate) {
      if (!endDate || !moment(endDate).isValid()) return null;
      return moment(endDate).add(CLAIM_CLOSE_OFFSET_DAYS, "days").format(VALUE_FORMAT);
    },
    calculationWindowDefault(claimsCloseDate) {
      if (!claimsCloseDate || !moment(claimsCloseDate).isValid()) return null;
      return moment(claimsCloseDate).add(RULE_CALCULATION_WINDOW_DAYS, "days").format(VALUE_FORMAT);
    },
    isAfter(later, earlier) {
      return !later || !earlier || moment(later).isAfter(moment(earlier));
    },
    isOnOrAfter(later, earlier) {
      return !later || !earlier || !moment(later).isBefore(moment(earlier));
    },
    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>

<style scoped>
.claim-timeline-step,
.form,
.copy,
.date-section {
  display: flex;
  flex-direction: column;
}

.claim-timeline-step,
.form {
  gap: 24px;
}

.copy {
  gap: 4px;
}

.date-section {
  gap: 16px;
}

.date-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.date-input {
  flex: 1 1 0;
  min-width: 0;
}
</style>
